import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Stack,
  Group,
  Text,
  SegmentedControl,
  FileInput,
  Alert,
  TagsInput,
  Badge,
} from '@mantine/core'
import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { secretsApi } from '@/api'
import { notifications } from '@mantine/notifications'
import { IconUpload, IconAlertCircle, IconKey, IconRefresh } from '@tabler/icons-react'
import type { Secret } from '@/types'

interface UpdateSecretModalProps {
  opened: boolean
  onClose: () => void
  secret: Secret
  onSuccess: () => void
}

export function UpdateSecretModal({ opened, onClose, secret, onSuccess }: UpdateSecretModalProps) {
  const [name, setName] = useState(secret.Spec.Name)
  const [inputType, setInputType] = useState<'text' | 'file'>('text')
  const [secretText, setSecretText] = useState('')
  const [secretFile, setSecretFile] = useState<File | null>(null)
  const [labels, setLabels] = useState<string[]>([])

  useEffect(() => {
    if (secret) {
      setName(secret.Spec.Name)

      // Convert labels object to array of "key=value" strings
      if (secret.Spec.Labels) {
        const labelArray = Object.entries(secret.Spec.Labels).map(([key, value]) => `${key}=${value}`)
        setLabels(labelArray)
      } else {
        setLabels([])
      }
    }
  }, [secret])

  const updateMutation = useMutation({
    mutationFn: async () => {
      let data = ''

      if (inputType === 'text') {
        // For text input, base64 encode the secret
        data = btoa(secretText)
      } else if (inputType === 'file' && secretFile) {
        // For file input, read the file and base64 encode it
        const arrayBuffer = await secretFile.arrayBuffer()
        const bytes = new Uint8Array(arrayBuffer)
        const binaryString = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('')
        data = btoa(binaryString)
      }

      // Convert labels array to object
      const labelsObj: Record<string, string> = {}
      labels.forEach((label) => {
        const [key, value] = label.split('=')
        if (key && value) {
          labelsObj[key.trim()] = value.trim()
        }
      })

      const { data: response } = await secretsApi.update(secret.ID, {
        Name: name,
        Data: data,
        Labels: Object.keys(labelsObj).length > 0 ? labelsObj : undefined,
      })
      return response
    },
    onSuccess: () => {
      notifications.show({
        title: 'Secret updated',
        message: `Secret ${name} has been updated successfully`,
        color: 'green',
      })
      onSuccess()
      resetForm()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Failed to update secret'

      // Backend returns "Secrets are immutable" error
      if (errorMessage.includes('immutable')) {
        notifications.show({
          title: 'Cannot update secret',
          message: 'Secrets are immutable in Docker Swarm. Delete and recreate the secret instead.',
          color: 'orange',
          autoClose: 8000,
        })
      } else {
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        })
      }
    },
  })

  const resetForm = () => {
    setSecretText('')
    setSecretFile(null)
    setInputType('text')
  }

  const handleSubmit = () => {
    // Validation
    if (!name) {
      notifications.show({
        title: 'Validation error',
        message: 'Please provide a secret name',
        color: 'red',
      })
      return
    }

    if (inputType === 'text' && !secretText) {
      notifications.show({
        title: 'Validation error',
        message: 'Please provide secret data',
        color: 'red',
      })
      return
    }

    if (inputType === 'file' && !secretFile) {
      notifications.show({
        title: 'Validation error',
        message: 'Please select a file',
        color: 'red',
      })
      return
    }

    // Check name format
    const nameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_.-]*$/
    if (!nameRegex.test(name)) {
      notifications.show({
        title: 'Validation error',
        message: 'Secret name must start with alphanumeric and contain only alphanumeric, underscore, hyphen, or dot',
        color: 'red',
      })
      return
    }

    if (name.length > 64) {
      notifications.show({
        title: 'Validation error',
        message: 'Secret name must be 64 characters or less',
        color: 'red',
      })
      return
    }

    updateMutation.mutate()
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Update Secret" size="lg">
      <Stack>
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          <strong>Note:</strong> In Docker Swarm, secrets are immutable. This operation creates a
          new version of the secret. You may need to update services to use the new version.
        </Alert>

        <Group>
          <Badge variant="light" leftSection={<IconKey size={14} />}>
            Current Version: {secret.Version.Index}
          </Badge>
          <Badge variant="light" color="blue" leftSection={<IconRefresh size={14} />}>
            New Version: {secret.Version.Index + 1}
          </Badge>
        </Group>

        <TextInput
          label="Secret Name"
          placeholder="my-secret"
          description="Alphanumeric, underscore, hyphen, or dot (max 64 characters)"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          leftSection={<IconKey size={16} />}
          disabled
        />

        <div>
          <Text size="sm" fw={500} mb="xs">
            Input Type
          </Text>
          <SegmentedControl
            value={inputType}
            onChange={(value) => setInputType(value as 'text' | 'file')}
            data={[
              { label: 'Text', value: 'text' },
              { label: 'File', value: 'file' },
            ]}
            fullWidth
          />
        </div>

        {inputType === 'text' ? (
          <Textarea
            label="New Secret Data"
            placeholder="Enter new secret data..."
            description="The data will be encrypted before storage"
            value={secretText}
            onChange={(e) => setSecretText(e.currentTarget.value)}
            required
            minRows={4}
            maxRows={8}
          />
        ) : (
          <FileInput
            label="New Secret File"
            placeholder="Choose a file"
            description="Upload a file to use as secret data (max 500KB)"
            value={secretFile}
            onChange={setSecretFile}
            required
            leftSection={<IconUpload size={16} />}
            accept="*/*"
          />
        )}

        <TagsInput
          label="Labels"
          placeholder="Enter labels as key=value"
          description="Press Enter after each label"
          value={labels}
          onChange={setLabels}
          splitChars={[',', ' ', ';']}
          clearable
        />

        {secretFile && (
          <Alert color="blue">
            Selected file: <strong>{secretFile.name}</strong> ({(secretFile.size / 1024).toFixed(2)} KB)
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={updateMutation.isPending}>
            Update Secret (Rotate)
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
